/**
 * Календарът на РОДИТЕЛЯ: ваканциите веднъж, часовете — на всяко дете.
 *
 * Решение на owner (16.09.2026): „общите ваканции и празници + часовете на ВСЯКО
 * дете. Цялото семейство с един поглед." Без превключвател „избери дете".
 *
 * 🔴 Нищо тук не разгъва часове само. Разгъването, спирането на последното
 * занятие и потискането във ваканция са в `buildTeacherCalendarEvents` — същата
 * функция, която ползват учителят и админът. Втора реализация би се разминала
 * при първата промяна, а разликата се вижда само на екрана на родителя, тоест
 * най-късно.
 */

import type { CalendarEvent } from '../components/ui/CalendarWidget';
import type { TeacherScheduleSlotView, TeacherHolidayView } from '@shared/types';
import { buildTeacherCalendarEvents, type TeacherCalendarOptions } from './teacherCalendar';

export interface ParentCalendarChild {
  childId: string;
  childName: string;
  /** Негрупираните слотове от `getChildSchedule.calendarSlots`. */
  slots: TeacherScheduleSlotView[];
  /** Цветът на маркерите на това дете (`childMarkColors`). */
  color: string;
}

export interface ParentCalendarResult {
  events: CalendarEvent[];
  /** Децата, които реално имат какво да покажат — входът на легендата. */
  legend: Array<{ childId: string; childName: string; color: string; hasSchedule: boolean }>;
}

/** Ключ за сравнение на две събития — датата и текстът, не референцията. */
const keyOf = (e: CalendarEvent) => `${e.date}|${e.label}`;

/**
 * Събитията за таблото на родителя.
 *
 * ⚠️ Ваканциите се строят ВЕДНЪЖ, с празен списък слотове, и после се вадят от
 * резултата на всяко дете. Така те се рисуват по един път на ден, а потискането
 * на часовете във ваканция си остава вътре в общата функция — тя вижда
 * ваканциите при всяко дете, просто маркерите им не се дублират.
 *
 * 🔴 Етикетът на часа носи ИМЕТО на детето. Без него две деца в един клас дават
 * два еднакви етикета, панелът на деня ги показва като повторение, а родителят
 * не може да каже чий е кой — точно това, което „различими" трябва да значи при
 * маркер от 4 пиксела.
 */
export function buildParentCalendarEvents(
  children: ParentCalendarChild[],
  holidays: TeacherHolidayView[],
  opts: TeacherCalendarOptions,
): ParentCalendarResult {
  const holidayEvents = buildTeacherCalendarEvents([], holidays, opts);
  const holidayKeys = new Set(holidayEvents.map(keyOf));

  const events: CalendarEvent[] = [...holidayEvents];
  const legend: ParentCalendarResult['legend'] = [];

  for (const child of children) {
    const all = buildTeacherCalendarEvents(child.slots, holidays, opts);
    const own = all.filter((e) => !holidayKeys.has(keyOf(e)));

    /**
     * Пълното място на урока — сграда · етаж · стая.
     *
     * Решение на owner (16.09.2026): при кликване на деня се показват ВСИЧКИ
     * данни за местоположението. В краткия етикет остава само стаята, защото
     * той се рисува и в „Предстоящи", където редът се реже.
     *
     * ⚠️ Слотовете на едно дете може да са в различни стаи, затова мястото се
     * взима от слота, съвпадащ по ЧАС, а не от първия.
     */
    const placeByTime = new Map<string, string>();
    for (const s of child.slots) {
      const place = [s.venueName, s.floor, s.roomName].filter(Boolean).join(' · ');
      if (place) placeByTime.set(`${s.startTime}–${s.endTime}`, place);
    }

    for (const e of own) {
      const hit = [...placeByTime.entries()].find(([time]) => e.label.includes(time));
      events.push({
        ...e,
        color: child.color,
        label: `${child.childName} · ${e.label}`,
        ...(hit ? { details: hit[1] } : {}),
      });
    }

    // ⚠️ Дете БЕЗ разписание остава в легендата, с отметка че няма часове.
    // Тихо изчезване е по-лошо: родителят не може да различи „няма часове" от
    // „екранът пропусна детето ми".
    legend.push({
      childId: child.childId,
      childName: child.childName,
      color: child.color,
      hasSchedule: own.length > 0,
    });
  }

  return { events, legend };
}
